/**
 * @see static/scripts/legacy/sitesmart-live-search-1-2.js for legacy code
 */
import Debounce from "@/lib/Debounce.ts";
import n, { N } from "@/lib/n.ts";
import axios, { type AxiosResponse } from "axios";

export default class LiveSearch
{
    #inputs: N;
    #wrapper: N|null = null;
    #backdrop: N|null = null;
    #version: number = 1;

    #resultClasses: { [key: string]: string } = {
        v2: 'sitesmart-cms-search-result--v2',
        open: 'sitesmart-cms-search-result--open',
        backdropOpen: 's-ec-search-result-live__backdrop--open',
    };

    constructor()
    {
        this.#version = this.findVersion();
        this.#inputs = this.getInputNodes(this.#version) as N;

        this.initBackdrop();
        this.initWrapper();

        Debounce.event('input', this.getInputSelector(), 250, (ev: Event) => {
            this.handleLiveSearchInput(ev as InputEvent);
        });

        const versionClickTarget: string = (this.#version === 2)
            ? '.s-ec-search-result-live__view-all'
            : '.sitesmart-cms-search-result__view-all';
        const clickTargets = [
            versionClickTarget,
            `input[data-sitesmart-live-search-${this.#version}]`,
        ];

        n(clickTargets.join(','), 'body').on('click', (ev: Event) => {
            this.handleLiveSearchClick(ev as MouseEvent);
        });

        Debounce.event('resize', window, 150, () => {
            this.setLiveSearchResultPosition();
        });
    }

    setLiveSearchResultPosition(): void
    {
        if (this.#wrapper !== null && this.#inputs) {
            const liveSearchResult = this.#wrapper;
            const target = this.#inputs;
            const posData = {
                y: (target?.top as number) ?? 0,
                x: (target?.left as number) ?? 0,
                width: (target?.width as number) ?? 0,
                height: (target?.height as number) ?? 0,
            };

            liveSearchResult
                .css('top', `${posData?.y + posData?.height}px`)
                .css('min-width', `${posData.width}px`);
        }
    }

    handleLiveSearchClick(ev: MouseEvent)
    {
        const target = n(ev.target);

        if (!this.#wrapper?.hasClass?.(this.#resultClasses.open)
            && target.hasClass('live-active')) {

            this.#wrapper?.addClass?.(this.#resultClasses.open);

            if (this.#version === 2
                && this.#backdrop
                && this.#resultClasses.backdropOpen) {
                this.#backdrop.addClass(this.#resultClasses.backdropOpen);
            }

            this.setLiveSearchResultPosition();

        } else {

            if (target?.parent?.('.s-ec-search-result-live__view-all')?.length
               || target?.parent?.('.sitesmart-cms-search-result__view-all')?.length) {

                this.#inputs
                    ?.parent?.('form.sitesmart-cms-search-form, form.sitesmart-ecommerce-search-form')
                    ?.trigger('submit');

            }

        }
    }

    handleLiveSearchInput(ev: InputEvent)
    {
        const searchInput = n(ev.target);
        const parent = searchInput.parent();
        const node = searchInput.getNode<HTMLInputElement>();
        const search = node?.value;
        const url = searchInput.getAttribute(`data-sitesmart-live-search-${this.#version}`) as string;
        const showOnlyInStock = searchInput.getAttribute('data-show-only-in-stock') ?? '0';

        searchInput.removeClass('live-active');
        searchInput.addClass('live-active');
        if (parent?.length) {
            parent.removeClass('sitesmart-cms-search-result__target');
            parent.addClass('sitesmart-cms-search-result__target');
        }
        if (search?.length && search?.length > 1) {

            axios.get(url, {
                params: {
                    search,
                    version: this.#version,
                    inStock: showOnlyInStock
                }
            }).then((r: AxiosResponse) => {
                this.#wrapper?.html?.(r?.data ?? "");
                this.#wrapper?.addClass?.(this.#resultClasses.open);
                if (this.#wrapper?.hasClass(this.#resultClasses.v2)) {
                    if (this.#version !== 2) {
                        this.#wrapper?.removeClass(this.#resultClasses.v2);
                    } else {
                        this.#wrapper?.addClass(this.#resultClasses.v2);
                    }
                }
                if (this.#version === 2 && this.#backdrop && this.#resultClasses.backdropOpen) {
                    this.#backdrop.addClass(this.#resultClasses.backdropOpen);
                }
                this.setLiveSearchResultPosition();
            });

        } else {
            this.#wrapper?.html?.('');
            this.#wrapper?.removeClass(this.#resultClasses.open);
            if (this.#version === 2 && this.#backdrop && this.#resultClasses.backdropOpen) {
                this.#backdrop.removeClass(this.#resultClasses.backdropOpen);
            }
            this.#inputs?.removeClass?.('live-active');
        }
    }

    initBackdrop()
    {
        if (this.#version === 2) {
            const el: N = n('<div />');
            el.addClass('s-ec-search-result-live__backdrop');
            el.css('display', 'none');
            this.#backdrop = el;
            n('body').append(this.#backdrop);
        }
    }

    initWrapper()
    {
        this.#wrapper = n('<div />');
        this.#wrapper.addClass('sitesmart-cms-search-result');
        n('body').append(this.#wrapper);
    }

    findVersion(): number
    {
        return (this.getInputNodes(2)?.length) ? 2 : 1;
    }

    getInputNodes(version: number): null|N
    {
        return n(this.getInputSelector(version)) ?? null;
    }

    getInputSelector(version: number|null = null): string
    {
        version ??= this.#version;
        return `input[data-sitesmart-live-search-${version}]`;
    }
}
